import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import srLocale from "date-fns/locale/sr";
import {
  getAllOfficialJournals,
  editNatMeasure,
  updateNatMeasureOJ,
  updateRealisationDate,
  updateRealisationStatusNote,
} from "../../../../server/server";
import { notif } from "../../../utils/util";
import { parse, isValid } from "date-fns";

const MIN_YEAR = 1900;

const OjModal = ({
  open,
  register,
  onClose,
  tempRealisation,
  noteData,
  disabled,
  quarter,
}: any) => {
  const [journals, setJournals] = useState<any>([]);
  const [selectedJournal, setSelectedJournal] = useState<any>(null);
  const [OJNumber, setOJNumber] = useState<any>(null);
  const [OJPagesNumber, setOJPagesNumber] = useState<any>(null);
  const [dateOfOJ, setDateOfOJ] = useState<any>(null);
  const [inForceOJ, setInForceOJ] = useState<any>(null);
  const [inUseOJ, setInUseOJ] = useState<any>(null);
  const [adopByGovDate, setAdopByGovDate] = useState<any>(
    tempRealisation || null,
  );
  const [errorMap, setErrorMap] = useState({
    selectedJournal: "",
    OJNumber: "",
    OJPagesNumber: "",
    dateOfOJ: "",
    inForceOJ: "",
    inUseOJ: "",
  });

  useEffect(() => {
    if (open) {
      loadAllJournals();
      loadRegisterData();
    }
  }, [open, register]);

  const loadAllJournals = async () => {
    const resp = await getAllOfficialJournals();
    if (resp?.status === 200) {
      setJournals(resp?.data);
    }
  };

  useEffect(() => {
    if (!open) return;

    const minAdop = adopByGovDate instanceof Date ? adopByGovDate : null;

    if (dateOfOJ && minAdop && dateOfOJ < minAdop) {
      setErrorMap((prev) => ({
        ...prev,
        dateOfOJ: `Датум не може бити пре ${formatDate(minAdop)}`,
      }));
    }
    if (inForceOJ && dateOfOJ && inForceOJ < dateOfOJ) {
      setErrorMap((prev) => ({
        ...prev,
        inForceOJ: `Датум не може бити пре ${formatDate(dateOfOJ)}`,
      }));
    }
    if (inUseOJ && inForceOJ && inUseOJ < inForceOJ) {
      setErrorMap((prev) => ({
        ...prev,
        inUseOJ: `Датум не може бити пре ${formatDate(inForceOJ)}`,
      }));
    }
  }, [dateOfOJ, inForceOJ, inUseOJ, adopByGovDate]);

  const parseDate = (val: any): Date | null => {
    if (!val && val !== 0) return null;
    try {
      if (val instanceof Date && !isNaN(val.getTime())) return val;
      if (typeof val === "number") {
        const d = new Date(val);
        if (!isNaN(d.getTime()) && d.getFullYear() >= MIN_YEAR) return d;
        return null;
      }
      if (typeof val === "string") {
        const raw = val.trim();
        const parts = raw.split(/[.\-\/\s]/).filter(Boolean);
        if (parts.length >= 3 && parts[2].length === 2) return null;
        const fmts = ["dd/MM/yyyy", "dd.MM.yyyy", "dd/MM/yy", "dd.MM.yy"];
        for (const f of fmts) {
          const parsed = parse(val, f, new Date());
          if (isValid(parsed) && parsed.getFullYear() >= MIN_YEAR)
            return parsed;
        }
        const iso = new Date(val);
        if (!isNaN(iso.getTime()) && iso.getFullYear() >= MIN_YEAR) return iso;
      }
    } catch (e) {
      console.warn("parseDate error:", e);
    }
    return null;
  };

  const loadRegisterData = () => {
    if (register) {
      const oj =
        register?.natMeasureOfficialJurnal || register?.journal || null;

      setSelectedJournal(oj);
      setOJNumber(register?.natMeasureOJNumber || null);
      setOJPagesNumber(register?.natMeasureOJPagesNumber || null);

      setErrorMap({
        selectedJournal: "",
        OJNumber: "",
        OJPagesNumber: "",
        dateOfOJ: "",
        inForceOJ: "",
        inUseOJ: "",
      });

      const hasAnyError = Object.values(errorMap).some(
        (msg) => msg && msg.length > 0,
      );
      if (hasAnyError) {
        notif("Исправите грешке у форми пре чувања", "error");
        return;
      }

      setDateOfOJ(parseDate(register?.natMeasureDateOfOJ));
      setInForceOJ(parseDate(register?.natMeasureInForceOJ));
      setInUseOJ(parseDate(register?.natMeasureInUseOJ));

      setAdopByGovDate(parseDate(register?.natMeasureAdopByGovDate));
    } else {
      setErrorMap({
        selectedJournal: "",
        OJNumber: "",
        OJPagesNumber: "",
        dateOfOJ: "",
        inForceOJ: "",
        inUseOJ: "",
      });
    }
  };
  const formatDate = (date: Date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  const handleJournalChange = (e: any, prm: any) => {
    setSelectedJournal(prm as any);
    setErrorMap((prev: any) => ({ ...prev, selectedJournal: "" }));
  };

  const sanitizeNumberInput = (val: string) =>
    val ? val.replace(/[^0-9\-\.,\/()]/g, "") : val;

  const handleOJNumberChange = (e: any) => {
    const sanitized = sanitizeNumberInput(e.target?.value || "");
    setOJNumber(sanitized);
    setErrorMap((prev: any) => ({ ...prev, OJNumber: "" }));
  };

  const handleOJPageNumberChange = (e: any) => {
    const sanitized = sanitizeNumberInput(e.target?.value || "");
    setOJPagesNumber(sanitized);
    setErrorMap((prev: any) => ({ ...prev, OJPagesNumber: "" }));
  };
  const handleSaveNote = async () => {
    const noteResponse = await updateRealisationStatusNote({
      nationalMeasureId: parseInt(register.natMeasureId),
      name: noteData.name,
      id: noteData.id || undefined,
      reportId: register.id || undefined,
      quarter: quarter || undefined,
      status: register.status === "Реализовано" ? true : false,
    });

    if (noteResponse.status === 201) {
      onClose();
    } else {
      notif("Грешка при чувању напомене.", "error");
    }
  };

  const handleSave = async () => {
    const hasAnyError = Object.values(errorMap).some(
      (msg) => msg && msg.length > 0,
    );
    if (hasAnyError) {
      notif("Исправите грешке у форми пре чувања", "error");
      return;
    }

    if (dateOfOJ < tempRealisation) {
      const formattedDate = formatDate(tempRealisation);

      setErrorMap((prev) => ({
        ...prev,
        dateOfOJ: `Датум не може бити пре ${formattedDate}`,
      }));

      notif(
        `Грешка: Датум објаве у СГ не може бити пре ${formattedDate}`,
        "error",
      );
      return;
    }

    const isValidDateValue = (d: any) =>
      d instanceof Date && !isNaN(d.getTime()) && d.getFullYear() >= MIN_YEAR;

    if (
      (dateOfOJ && !isValidDateValue(dateOfOJ)) ||
      (inForceOJ && !isValidDateValue(inForceOJ)) ||
      (inUseOJ && !isValidDateValue(inUseOJ))
    ) {
      setErrorMap((prev: any) => ({
        ...prev,
        dateOfOJ:
          dateOfOJ && !isValidDateValue(dateOfOJ)
            ? "Погрешан формат датума"
            : prev.dateOfOJ,
        inForceOJ:
          inForceOJ && !isValidDateValue(inForceOJ)
            ? "Погрешан формат датума"
            : prev.inForceOJ,
        inUseOJ:
          inUseOJ && !isValidDateValue(inUseOJ)
            ? "Погрешан формат датуma"
            : prev.inUseOJ,
      }));
      notif("Грешка у формату датума", "error");
      return;
    }

    const newErrorMap = {
      selectedJournal: selectedJournal ? "" : "Поље је обавезно",
      OJNumber: OJNumber ? "" : "Поље је обавезно",
      OJPagesNumber: OJPagesNumber ? "" : "Поље је обавезно",
      dateOfOJ: dateOfOJ ? "" : "Поље је обавезно",
      inForceOJ: inForceOJ ? "" : "Поље је обавезно",
      inUseOJ: inUseOJ ? "" : "Поље је обавезно",
    };

    setErrorMap(newErrorMap);

    if (!Object.values(newErrorMap).some((e) => e)) {
      try {
        let realisationResp;
        if (tempRealisation) {
          realisationResp = await updateRealisationDate({
            id: parseInt(register.natMeasureId),
            adopByGovDate: tempRealisation,
            reportId: register?.id,
          });
        }

        const resp = await updateNatMeasureOJ({
          id: register?.natMeasureId,
          ojId: selectedJournal?.id,
          OJNumber: OJNumber,
          OJPagesNumber: OJPagesNumber,
          dateOfOJ,
          inForceOJ,
          inUseOJ,
          reportId: register?.id,
        });

        if (resp.status === 201 && realisationResp?.status === 201) {
          const resp = await handleSaveNote();
          notif(
            "Службени гласник и датум реализације су успешно ажурирани",
            "success",
          );
          onClose();
        } else if (resp.status === 201) {
          notif("Службени гласник је успешно ажуриран", "success");
          onClose();
        } else {
          notif(resp?.data?.message || "Грешка при ажурирању", "error");
        }
      } catch (error: any) {
        if (error?.response?.status === 500) {
          notif("Грешка у формату датума", "error");
        } else if (error?.response?.data?.message) {
          notif(error.response.data.message, "error");
        } else {
          notif("Грешка при ажурирању Службеног гласника", "error");
        }
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Измена Службеног гласника</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 mt-4">
          <div className="flex gap-4 h-[70px]">
            <Autocomplete
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                );
              }}
              noOptionsText={"Нема резултата"}
              value={selectedJournal}
              size="small"
              onChange={(event: any, newValue: any) =>
                handleJournalChange(event, newValue)
              }
              id="controllable-states-demo"
              options={journals}
              getOptionLabel={(option) => option?.name || ""}
              sx={{ width: 340 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Службени гласник"
                  error={Boolean(errorMap?.selectedJournal)}
                  helperText={errorMap?.selectedJournal}
                />
              )}
            />
            <TextField
              label="Број"
              id="outlined-size-small"
              type="text"
              name="OJNumber"
              size="small"
              sx={{ width: 180 }}
              helperText={errorMap?.OJNumber}
              error={Boolean(errorMap?.OJNumber)}
              onFocus={() => setErrorMap({ ...errorMap, OJNumber: "" })}
              value={OJNumber || ""}
              onChange={handleOJNumberChange}
              onPaste={(e: any) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("Text");
                handleOJNumberChange({ target: { value: pasted } });
              }}
            />
            <TextField
              label="Редни број објаве у СГ"
              id="outlined-size-small"
              type="text"
              name="OJPageNumber"
              size="small"
              sx={{ width: 200 }}
              helperText={errorMap?.OJPagesNumber}
              error={Boolean(errorMap?.OJPagesNumber)}
              onFocus={() => setErrorMap({ ...errorMap, OJPagesNumber: "" })}
              value={OJPagesNumber || ""}
              onChange={handleOJPageNumberChange}
              onPaste={(e: any) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("Text");
                handleOJPageNumberChange({ target: { value: pasted } });
              }}
            />
          </div>

          <div className="flex gap-4 flex-1">
            <div className="flex flex-col justify-center flex-1">
              <p className="text-center text-sm text-[#04438b] mb-2">
                Датум објаве у СГ
              </p>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={srLocale}
              >
                <DatePicker
                  label="Датум објаве"
                  minDate={
                    adopByGovDate ? new Date(adopByGovDate) : tempRealisation
                  }
                  value={
                    dateOfOJ instanceof Date &&
                    !isNaN(dateOfOJ.getTime()) &&
                    dateOfOJ.getFullYear() >= MIN_YEAR
                      ? dateOfOJ
                      : null
                  }
                  inputFormat="dd.MM.yyyy"
                  onChange={(newValue) => {
                    setDateOfOJ(newValue);
                    setErrorMap((prev: any) => ({
                      ...prev,
                      dateOfOJ: "",
                      inForceOJ: "",
                      inUseOJ: "",
                    }));
                    const min = adopByGovDate ? new Date(adopByGovDate) : null;

                    if (newValue && min && newValue < min) {
                      setErrorMap((prev) => ({
                        ...prev,
                        dateOfOJ: `Датум не може бити пре ${formatDate(min)}`,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      fullWidth
                      {...params}
                      error={Boolean(errorMap?.dateOfOJ)}
                      helperText={errorMap?.dateOfOJ}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>

            <div className="flex flex-col justify-center flex-1">
              <p className="text-center text-sm text-[#04438b] mb-2">
                Ступање на снагу
              </p>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={srLocale}
              >
                <DatePicker
                  label="Датум ступања на снагу"
                  minDate={
                    dateOfOJ instanceof Date &&
                    !isNaN(dateOfOJ.getTime()) &&
                    dateOfOJ.getFullYear() >= MIN_YEAR
                      ? dateOfOJ
                      : undefined
                  }
                  value={
                    inForceOJ instanceof Date &&
                    !isNaN(inForceOJ.getTime()) &&
                    inForceOJ.getFullYear() >= MIN_YEAR
                      ? inForceOJ
                      : null
                  }
                  inputFormat="dd.MM.yyyy"
                  onChange={(newValue) => {
                    setInForceOJ(newValue);
                    setErrorMap((prev: any) => ({
                      ...prev,
                      inForceOJ: "",
                      inUseOJ: "",
                    }));
                    const min =
                      dateOfOJ instanceof Date &&
                      !isNaN(dateOfOJ.getTime()) &&
                      dateOfOJ.getFullYear() >= MIN_YEAR
                        ? dateOfOJ
                        : null;

                    if (newValue && min && newValue < min) {
                      setErrorMap((prev) => ({
                        ...prev,
                        inForceOJ: `Датум не може бити пре ${formatDate(min)}`,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      fullWidth
                      {...params}
                      error={Boolean(errorMap?.inForceOJ)}
                      helperText={errorMap?.inForceOJ}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>

            <div className="flex flex-col justify-center flex-1">
              <p className="text-center text-sm text-[#04438b] mb-2">
                Почетак примене
              </p>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={srLocale}
              >
                <DatePicker
                  label="Датум примене"
                  minDate={
                    inForceOJ instanceof Date &&
                    !isNaN(inForceOJ.getTime()) &&
                    inForceOJ.getFullYear() >= MIN_YEAR
                      ? inForceOJ
                      : undefined
                  }
                  value={
                    inUseOJ instanceof Date &&
                    !isNaN(inUseOJ.getTime()) &&
                    inUseOJ.getFullYear() >= MIN_YEAR
                      ? inUseOJ
                      : null
                  }
                  inputFormat="dd.MM.yyyy"
                  onChange={(newValue) => {
                    setInUseOJ(newValue);
                    setErrorMap((prev: any) => ({ ...prev, inUseOJ: "" }));

                    const min =
                      inForceOJ instanceof Date &&
                      !isNaN(inForceOJ.getTime()) &&
                      inForceOJ.getFullYear() >= MIN_YEAR
                        ? inForceOJ
                        : null;

                    if (newValue && min && newValue < min) {
                      setErrorMap((prev) => ({
                        ...prev,
                        inUseOJ: `Датум не може бити пре ${formatDate(min)}`,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      fullWidth
                      {...params}
                      error={Boolean(errorMap?.inUseOJ)}
                      helperText={errorMap?.inUseOJ}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
      </DialogContent>
      <div className="flex flex-row justify-between items-center w-full mt-6 p-6">
        <div
          className="border border-[#81848f] hover:bg-[#81848f] hover:text-white cursor-pointer flex items-center justify-center border-redError text-redError text-base font-semibold rounded-lg w-[250px] h-[55px]"
          onClick={() => onClose()}
        >
          Oткажи
        </div>
        <Tooltip
          title={disabled ? "Тренутно су закључане измене" : ""}
          arrow
          placement="top"
        >
          <span>
            <div
              className={`${
                disabled ? "bg-[#e0e0e0]" : "bg-darkBlue"
              } cursor-pointer flex items-center justify-center ${
                disabled ? "text-[#a6a6a6]" : "text-white"
              } hover:opacity-90 text-base font-semibold rounded-lg w-[250px] h-[55px]`}
              onClick={() => {
                if (disabled) return;
                handleSave();
              }}
            >
              Сачувај
            </div>
          </span>
        </Tooltip>
      </div>
    </Dialog>
  );
};

export default OjModal;
